import { Component, OnInit } from '@angular/core';
import { StatefulComponent } from '../../components/stateful/stateful.component';
import { CommonModule } from '@angular/common';
import { NgbCarouselModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { setContainerTypeAction } from '../../ngrx/actions/global.actions';
import { GoogleReview } from '../../models/GoogleReview.model';
import { GoogleReviewsService } from '../../services/google-review.service';
import { RouterLink } from '@angular/router';
import { CustomerContactModalComponent } from '../../components/customer-contact-modal/customer-contact-modal.component';

interface ShopImage {
  src: string;
  alt: string;
  caption?: string;
}

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: true,
    imports: [CommonModule, NgbCarouselModule, RouterLink],
    providers: [GoogleReviewsService]
})
export class HomeComponent extends StatefulComponent implements OnInit {
  businessName = 'Kingdom Brothers';
  phone = '+14073500316';
  displayPhone = '(407) 350-0316';
  email = 'kingdombrothersauto88@gmail.com';
  address = '69 Oak Tree Drive, Chelsea, AL 35043';
  mapEmbedUrl: SafeResourceUrl;
  mapsApiKey = 'AIzaSyCsf0cSXQ3ZoegTCf5fnpSAOqOduEd0A84';
  googleReviews: GoogleReview[] = [];
  loadingReviews: boolean = true;
  averageRating: number = 0;
  totalReviews: number = 0;
  expandedReviews: Set<string> = new Set();
  expandedServices: Set<string> = new Set();
  
  services: { name: string; description: string }[] = [
    {
      name: 'Oil Change',
      description:
        'Premium oil and filter replacement that maximizes engine life and maintains warranty compliance.',
    },
    {
      name: 'Brake Service',
      description:
        'Comprehensive inspection, pad/rotor replacement, and fluid service for confident, quiet stopping power.',
    },
    {
      name: 'Tire Rotation',
      description:
        'Cross-pattern rotations that even out tread wear and extend the life of your tire investment.',
    },
    {
      name: 'Roadside Assistance',
      description:
        'Reliable help when you need it most to get you back on the road quickly.'
    },
    {
      name: 'Engine Diagnostics',
      description:
        'Computerized fault-code scanning and on-road analysis to pinpoint issues before they become costly repairs.',
    },
    {
      name: 'Battery Replacement',
      description:
        'Load-testing and high-quality battery install so you never get stuck with a surprise no-start.',
    },
    {
      name: 'Suspension & Steering Repairs',
      description:
        'Struts, shocks, ball joints, and alignments that return factory-smooth handling and ride comfort.',
    },
    {
      name: 'Air Conditioning Service',
      description:
        'Refrigerant recharge, leak detection, and performance testing to keep your cabin ice-cold year-round.',
    },
    {
      name: 'Transmission Service',
      description:
        'Fluid & filter change plus adaptive-shift reset to protect gears and ensure silky-smooth shifts.',
    },
    {
      name: 'Timing Belt Replacement',
      description:
        'Factory-scheduled belt, tensioner, and water-pump replacement that prevents catastrophic engine damage.',
    },
    {
      name: 'Radiator Service',
      description:
        'Coolant flush, pressure test, and hose inspection to keep operating temps in the safe zone.',
    },
    {
      name: 'Tune-Ups',
      description:
        'Spark plugs, filters, and ignition recalibration that restore lost power and maximize fuel economy.',
    },
    {
      name: 'Performance Modifications',
      description:
        'Cold-air intakes, exhaust upgrades, and custom ECU tuning tailored to your horsepower goals.',
    },
    {
      name: 'Superchargers & Turbos',
      description:
        'Complete forced-induction packages for dramatic, reliable power gains.',
    },
    {
      name: 'Fleet Maintenance',
      description:
        'Cost-efficient preventative care and rapid turnaround that keep your business vehicles on the road.',
    },
  ];

  shopImages: ShopImage[] = [
    {
      src: '../../assets/lobby.jpg',
      alt: 'Kingdom Brothers Auto Interior - Comfortable Waiting Area',
      caption: 'Comfortable waiting area with modern amenities'
    },
    {
      src: '../../assets/location.jpg',
      alt: 'Kingdom Brothers Auto Exterior - Shop Front',
      caption: 'Our professional auto repair facility'
    },
    {
      src: '../../assets/car1.jpg',
      alt: 'Customer car photo 1'
    },
    {
      src: '../../assets/shop3.jpg',
      alt: 'Kingdom Brothers Auto Signage'
    },
    {
      src: '../../assets/car2.jpg',
      alt: 'Customer car photo 2'
    },
    {
      src: '../../assets/shop2.jpg',
      alt: 'A line of customer cars'
    },
    {
      src: '../../assets/car3.jpg',
      alt: 'Customer car photo 3'
    },
    {
      src: '../../assets/shop1.jpg',
      alt: 'Kingdom Brothers Auto Signage'
    },
    {
      src: '../../assets/car4.jpg',
      alt: 'Customer car photo 4'
    },
    {
      src: '../../assets/shop-exterior.jpg',
      alt: 'Kingdom Brothers Auto Exterior - Shop Front'
    },
    {
      src: '../../assets/engine.jpg',
      alt: 'Engine Block Photo'
    },
    {
      src: '../../assets/shop4.jpg',
      alt: 'Kingdom Brothers Auto Facebook'
    },
  ];

  constructor(
    private sanitizer: DomSanitizer,
    private googleReviewsService: GoogleReviewsService,
    private ngbModal: NgbModal
  ) {
    super();
    
    // Create Google Maps embed URL
    const encodedAddress = encodeURIComponent(this.address);
    const mapUrl = `https://www.google.com/maps/embed/v1/place?key=${this.mapsApiKey}&q=${encodedAddress}&zoom=15`;
    this.mapEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(mapUrl);
  }

  ngOnInit(): void {
    this.dispatch(setContainerTypeAction({ containerType: 'fluid' }));
    this.loadGoogleReviews();
  }

  loadGoogleReviews(): void {
    this.googleReviewsService.getReviews().subscribe((reviews) => {
      this.googleReviews = reviews;
      this.loadingReviews = false;
    });
  }

  trackByAuthor(index: number, review: GoogleReview): string {
    return review.author_name + review.time;
  }

  getStars(rating: number): string[] {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push('★');
    }
    
    if (hasHalfStar && stars.length < 5) {
      stars.push('☆');
    }
    
    while (stars.length < 5) {
      stars.push('☆');
    }
    
    return stars;
  }

  openContactModal(): void {
    const modalRef = this.ngbModal.open(CustomerContactModalComponent, { centered: true });
    modalRef.componentInstance.modalRef = modalRef;
  }

  callBusiness(): void {
    window.open(`tel:${this.phone}`, '_self');
  }

  emailBusiness(): void {
    window.open(`mailto:${this.email}`, '_self');
  }

  getDirections(): void {
    const encodedAddress = encodeURIComponent(this.address);
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
    window.open(url, '_blank');
  }

  openFacebook() {
    const url = `https://www.facebook.com/profile.php?id=61575446685642`;
    window.open(url, '_blank');
  }

  openInstagram() {
    const url = `https://www.instagram.com/kingdom_brothers_auto`;
    window.open(url, '_blank');
  }

  toggleReviewExpansion(reviewId: string): void {
    if (this.expandedReviews.has(reviewId)) {
      this.expandedReviews.clear();
    } else {
      this.expandedReviews.clear();
      this.expandedReviews.add(reviewId);
    }
  }

  isReviewExpanded(reviewId: string): boolean {
    return this.expandedReviews.has(reviewId);
  }

  isServiceOpen(service: string): boolean {
    return this.expandedServices.has(service);
  }

  shouldShowReadMore(reviewText: string): boolean {
    return reviewText.length > 200;
  }

  toggleServiceCard(service: string): void {
    if (this.expandedServices.has(service)) {
      this.expandedServices.clear();
    } else {
      this.expandedServices.clear();
      this.expandedServices.add(service);
    }
  }

  getTruncatedText(reviewText: string): string {
    return reviewText.length > 200 ? reviewText.substring(0, 200) + '...' : reviewText;
  }
}